import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  getDepartments,
  getExhibitionSettings,
  getOccupationalGroups,
  getProfessions,
  getTitles,
  selectIsGoogleMapsApiEnabled
} from '@app/app.reducer';
import { FormValidators } from '@core/data-access/models/dynamic-form-field/dynamic-form-field-validators.model';
import {
  CheckboxField,
  DynamicFormField,
  DynamicFormFields,
  FieldKey,
  OptionKey
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FileKind, FileType } from '@core/data-access/models/file-upload.model';
import { FormFields, FormFieldsValue } from '@core/data-access/models/form/form-store.model';
import { Person, PersonType } from '@core/data-access/models/person.model';
import { TicketSendingOption } from '@core/data-access/models/shared/ticket-sending-options.model';
import { DynamicFormFieldBuilder } from '@core/data-access/services/dynamic-form-field/dynamic-form-field-builder.service';
import { DynamicFormFieldValidatorFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-field-validator-factory.service';
import {
  defaultCreateFormFieldSettings,
  isFieldDisabled,
  isFieldHidden,
  setSendToOwnerFieldVisibility
} from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import {
  ticketHolderEmailAndVerifyEmailFormFieldParametersMapper,
  ticketHolderFormDynamicFormFieldParametersMapper
} from '@core/utils/dynamic-form-field/dynamic-form-field-parameters-mapper';
import { isGDPRConsentFieldChecked } from '@core/utils/form-fields/form-field-helpers';
import { Store } from '@ngrx/store';
import { BookingTariff } from '@products/models/booking.model';
import { SendingOptionModel } from '@products/models/product-selection.model';
import { DocumentUploadType, TicketPersonId } from '@products/models/tariff.model';
import { AppConstants } from '@shared/app-constants';
import { ExhibitionSettingModel, FieldSetting, FieldSettings } from '@store/customization/customization.interfaces';
import { SelectOption } from '@store/exhibition/exhibition.interface';
import { getSendingOptions } from '@store/products/product-selection/product-selection.selectors';
import {
  filter as _filter,
  cloneDeep,
  defaultTo,
  find,
  findIndex,
  forEach,
  forIn,
  head,
  includes,
  isEmpty,
  isNil,
  maxBy,
  pullAt,
  sortBy,
  toLower,
  upperFirst
} from 'lodash';
import { combineLatest, first, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TicketHolderDynamicFormFieldsFactory {
  readonly #store = inject(Store);
  readonly #dynamicFormFieldBuilder = inject(DynamicFormFieldBuilder);
  readonly #dynamicFormFieldValidatorFactory = inject(DynamicFormFieldValidatorFactory);

  createDynamicTicketHolderFormFields$(
    bookingTariff: BookingTariff,
    selectedTicketSendingOption: TicketSendingOption,
    formFields: FormFields,
    person: Person,
    isReadOnlyBuyerFormFields: boolean
  ) {
    return combineLatest({
      isGoogleMapsApiEnabled: this.#store.select(selectIsGoogleMapsApiEnabled),
      exhibitionSettings: this.#store.select(getExhibitionSettings),
      ticketSendingOptions: this.#store.select(getSendingOptions),
      titles: this.#store.select(getTitles),
      professions: this.#store.select(getProfessions),
      departments: this.#store.select(getDepartments),
      occupationalGroups: this.#store.select(getOccupationalGroups)
    }).pipe(
      first(
        ({ exhibitionSettings, ticketSendingOptions }) => !isEmpty(exhibitionSettings) && !isEmpty(ticketSendingOptions)
      ),
      map(
        ({
          isGoogleMapsApiEnabled,
          exhibitionSettings,
          ticketSendingOptions,
          titles,
          professions,
          departments,
          occupationalGroups
        }) => {
          const { ticketPersonId, documentUploadType } = bookingTariff;
          const isReadOnly = person?.type === PersonType.BuyerIsHolder && isReadOnlyBuyerFormFields;

          const dynamicTicketHolderFormFields = this.createDynamicTicketHolderFormFields(
            ticketPersonId,
            formFields,
            isReadOnly,
            isGoogleMapsApiEnabled,
            exhibitionSettings,
            titles,
            professions,
            departments,
            occupationalGroups,
            selectedTicketSendingOption
          );

          const { order } = maxBy(dynamicTicketHolderFormFields, 'order')!;

          const fixedTicketHolderFormFields = this.createFixedTicketHolderFormFields(
            documentUploadType,
            formFields,
            person?.type,
            selectedTicketSendingOption,
            ticketSendingOptions,
            order
          );

          const ticketHolderDynamicFormFields = [...dynamicTicketHolderFormFields, ...fixedTicketHolderFormFields];

          return sortBy(ticketHolderDynamicFormFields, 'order');
        }
      )
    );
  }

  updateFormOnTicketSendingOptionChange(
    dynamicFormFields: DynamicFormFields,
    form: FormGroup,
    person: Person,
    formFields: FormFields,
    ticketPersonId: TicketPersonId,
    exhibitionSettings: ExhibitionSettingModel,
    ticketSendingOptions: SendingOptionModel[],
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const clonedDynamicFormFields = cloneDeep(dynamicFormFields);

    this.setFirstAndLastNameRequired(
      clonedDynamicFormFields,
      form,
      ticketPersonId,
      exhibitionSettings,
      selectedTicketSendingOption
    );

    this.setEmailAndVerifyEmailDynamicFormFields(
      clonedDynamicFormFields,
      form,
      formFields,
      ticketPersonId,
      exhibitionSettings,
      selectedTicketSendingOption
    );

    this.setSendToOwnerDynamicFormField(
      clonedDynamicFormFields,
      person?.type,
      ticketSendingOptions,
      selectedTicketSendingOption
    );

    return sortBy(clonedDynamicFormFields, 'order');
  }

  updateFormOnSendToOwnerChange(
    dynamicFormFields: DynamicFormFields,
    form: FormGroup,
    formFields: FormFields,
    ticketPersonId: TicketPersonId,
    exhibitionSettings: ExhibitionSettingModel,
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const clonedDynamicFormFields = cloneDeep(dynamicFormFields);

    this.setEmailAndVerifyEmailDynamicFormFields(
      clonedDynamicFormFields,
      form,
      formFields,
      ticketPersonId,
      exhibitionSettings,
      selectedTicketSendingOption
    );

    return sortBy(clonedDynamicFormFields, 'order');
  }

  updateFormOnGDPRConsentChange(dynamicFormFields: DynamicFormFields, formFields: FormFields) {
    const clonedDynamicFormFields = cloneDeep(dynamicFormFields);

    this.enableOrDisableCVFileUploadField(clonedDynamicFormFields, formFields);

    return sortBy(clonedDynamicFormFields, 'order');
  }

  private createDynamicTicketHolderFormFields(
    ticketPersonId: TicketPersonId,
    formFields: FormFields,
    isReadOnly: boolean,
    isGoogleMapsApiEnabled: boolean,
    exhibitionSettings: ExhibitionSettingModel,
    titles: SelectOption[],
    professions: SelectOption[],
    departments: SelectOption[],
    occupationalGroups: SelectOption[],
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const {
      tariffSpecificSettings,
      ticketOwnerSettings: { fieldSettings },
      isTelephoneValidationDisabled
    } = exhibitionSettings;

    const isEmailAndConfirmEmailMandatory = this.isEmailAndConfirmEmailMandatory(
      formFields,
      exhibitionSettings.emailIsMandatoryForMobileTicket,
      selectedTicketSendingOption
    );

    const isTicketRetrievalLinkTicketSendingOption =
      selectedTicketSendingOption === TicketSendingOption.TicketRetrievalLink;

    const tariffSpecificFieldSettings = find(
      tariffSpecificSettings,
      (tariffSpecificSetting) => tariffSpecificSetting.ticketPersonId === ticketPersonId
    )?.ticketOwnerSettings?.fieldSettings;

    const ticketHolderSettings = tariffSpecificFieldSettings || fieldSettings;
    const isCountryFieldVisible = ticketHolderSettings.Country.isVisible;

    const dynamicFormFields: DynamicFormFields = [];

    const createDynamicFormFieldMapper = this.#dynamicFormFieldBuilder.createDynamicFormFieldMapper();
    const createDynamicFormFieldParametersMapper = ticketHolderFormDynamicFormFieldParametersMapper(
      fieldSettings,
      isReadOnly,
      isGoogleMapsApiEnabled,
      isTelephoneValidationDisabled,
      isEmailAndConfirmEmailMandatory,
      isTicketRetrievalLinkTicketSendingOption,
      isCountryFieldVisible,
      titles,
      professions,
      departments,
      occupationalGroups
    );

    forIn(ticketHolderSettings, (fieldSetting, fieldKey: FieldKey) => {
      const createDynamicFormFieldFunction = createDynamicFormFieldMapper[fieldKey];

      const createDynamicFormFieldParameters = {
        fieldSetting,
        ...createDynamicFormFieldParametersMapper[fieldKey]
      };

      const dynamicFormField: DynamicFormField = createDynamicFormFieldFunction(createDynamicFormFieldParameters);

      if (dynamicFormField) {
        dynamicFormField.isHidden = isFieldHidden(dynamicFormField, formFields);
        dynamicFormField.isDisabled = isFieldDisabled(dynamicFormField, formFields);

        dynamicFormFields.push(dynamicFormField);
      }
    });

    return _filter(dynamicFormFields);
  }

  private createFixedTicketHolderFormFields(
    documentUploadType: DocumentUploadType,
    formFields: FormFields,
    personType: PersonType,
    selectedTicketSendingOption: TicketSendingOption,
    ticketSendingOptions: SendingOptionModel[],
    order: number
  ) {
    const { isSendToOwnerChecked, isSendToOwnerHidden } = setSendToOwnerFieldVisibility(
      personType,
      ticketSendingOptions,
      selectedTicketSendingOption
    );

    const sendToOwnerField = this.#dynamicFormFieldBuilder.creteSendToOwnerField({
      checkboxHeading: '',
      checkboxOptionLabel: `ticket-assignment.ticket-holder-form.checkbox.sending-option.${toLower(selectedTicketSendingOption)}.label`,
      order: 0,
      isSendToOwnerChecked,
      createFormFieldSettings: {
        ...defaultCreateFormFieldSettings,
        isHidden: isSendToOwnerHidden,
        columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
      }
    });

    const createdDynamicFormFields: DynamicFormFields = [sendToOwnerField];

    const hasDocumentUpload = includes([DocumentUploadType.Optional, DocumentUploadType.Mandatory], documentUploadType);

    if (hasDocumentUpload) {
      createdDynamicFormFields.push(
        ...this.createUploadDocumentDynamicFormFields(documentUploadType, formFields, order)
      );
    }

    forEach(createdDynamicFormFields, (createdDynamicFormField) => {
      createdDynamicFormField.isHidden = isFieldHidden(createdDynamicFormField, formFields);
      createdDynamicFormField.isDisabled = isFieldDisabled(createdDynamicFormField, formFields);
    });

    return createdDynamicFormFields;
  }

  private setSendToOwnerDynamicFormField(
    dynamicFormFields: DynamicFormFields,
    personType: PersonType,
    ticketSendingOptions: SendingOptionModel[],
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const sendToOwnerDynamicFormField = find(
      dynamicFormFields,
      ({ key }) => key === FieldKey.SendToOwner
    ) as CheckboxField;

    sendToOwnerDynamicFormField.isHidden = setSendToOwnerFieldVisibility(
      personType,
      ticketSendingOptions,
      selectedTicketSendingOption
    ).isSendToOwnerHidden;

    head(sendToOwnerDynamicFormField.options)!.label =
      `ticket-assignment.ticket-holder-form.checkbox.sending-option.${toLower(selectedTicketSendingOption)}.label`;
  }

  private setFirstAndLastNameRequired(
    dynamicFormFields: DynamicFormFields,
    form: FormGroup,
    ticketPersonId: TicketPersonId,
    exhibitionSettings: ExhibitionSettingModel,
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const {
      tariffSpecificSettings,
      ticketOwnerSettings: { fieldSettings }
    } = exhibitionSettings;

    const tariffSpecificFieldSettings = find(
      tariffSpecificSettings,
      (tariffSpecificSetting) => tariffSpecificSetting.ticketPersonId === ticketPersonId
    )?.ticketOwnerSettings?.fieldSettings;

    const ticketHolderSettings = tariffSpecificFieldSettings || fieldSettings;

    const isTicketRetrievalLinkTicketSendingOption =
      selectedTicketSendingOption === TicketSendingOption.TicketRetrievalLink;

    const isFirstNameRequired =
      ticketHolderSettings[FieldKey.FirstName]?.isMandatory || isTicketRetrievalLinkTicketSendingOption;
    const isLastNameRequired =
      ticketHolderSettings[FieldKey.LastName]?.isMandatory || isTicketRetrievalLinkTicketSendingOption;

    this.setIsFieldRequired(
      find(dynamicFormFields, ({ key }) => key === FieldKey.FirstName),
      form.get(FieldKey.FirstName),
      isFirstNameRequired
    );

    this.setIsFieldRequired(
      find(dynamicFormFields, ({ key }) => key === FieldKey.LastName),
      form.get(FieldKey.LastName),
      isLastNameRequired
    );
  }

  private setEmailAndVerifyEmailDynamicFormFields(
    dynamicFormFields: DynamicFormFields,
    form: FormGroup,
    formFields: FormFields,
    ticketPersonId: TicketPersonId,
    exhibitionSettings: ExhibitionSettingModel,
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const {
      tariffSpecificSettings,
      ticketOwnerSettings: { fieldSettings }
    } = exhibitionSettings;

    const tariffSpecificFieldSettings = find(
      tariffSpecificSettings,
      (tariffSpecificSetting) => tariffSpecificSetting.ticketPersonId === ticketPersonId
    )?.ticketOwnerSettings?.fieldSettings;

    const ticketHolderSettings = tariffSpecificFieldSettings || fieldSettings;

    const isEmailAndConfirmEmailMandatory = this.isEmailAndConfirmEmailMandatory(
      formFields,
      exhibitionSettings.emailIsMandatoryForMobileTicket,
      selectedTicketSendingOption
    );

    this.addOrRemoveEmailField(
      dynamicFormFields,
      form,
      formFields,
      FieldKey.Email,
      ticketHolderSettings,
      isEmailAndConfirmEmailMandatory
    );

    this.addOrRemoveEmailField(
      dynamicFormFields,
      form,
      formFields,
      FieldKey.MailConfirm,
      ticketHolderSettings,
      isEmailAndConfirmEmailMandatory
    );

    return sortBy(dynamicFormFields, 'order');
  }

  private setIsFieldRequired(dynamicFormField: DynamicFormField, formControl: AbstractControl, isRequired: boolean) {
    if (isNil(dynamicFormField)) {
      return;
    }

    dynamicFormField.isRequired = isRequired;

    isRequired ? formControl.addValidators([Validators.required]) : formControl.removeValidators([Validators.required]);

    formControl.updateValueAndValidity();
  }

  private isSendToOwnerFieldChecked(formFields: FormFields) {
    return formFields[FieldKey.SendToOwner]?.value[OptionKey.ToOwner]?.value;
  }

  private isEmailAndConfirmEmailMandatory(
    formFields: FormFields,
    emailIsMandatoryForMobileTicket: boolean,
    selectedTicketSendingOption: TicketSendingOption
  ) {
    const isSendToOwnerChecked = this.isSendToOwnerFieldChecked(formFields);
    const isEmailMandatoryForMobileTicketSendingOption =
      emailIsMandatoryForMobileTicket && selectedTicketSendingOption === TicketSendingOption.MobilePerOwner;
    const isTicketRetrievalLinkTicketSendingOption =
      selectedTicketSendingOption === TicketSendingOption.TicketRetrievalLink;

    return (
      isSendToOwnerChecked || isEmailMandatoryForMobileTicketSendingOption || isTicketRetrievalLinkTicketSendingOption
    );
  }

  private addOrRemoveEmailField(
    dynamicFormFields: DynamicFormFields,
    form: FormGroup,
    formFields: FormFields,
    fieldKey: FieldKey,
    ticketHolderSettings: FieldSettings,
    isEmailAndConfirmEmailMandatory: boolean
  ) {
    const dynamicFormFieldIndex = findIndex(dynamicFormFields, ({ key }) => key === fieldKey);

    const fieldSettingPropertyName = upperFirst(fieldKey);

    const { isVisible, isMandatory }: FieldSetting = ticketHolderSettings[fieldSettingPropertyName];

    const isRequired = isMandatory || isEmailAndConfirmEmailMandatory;

    const setRequiredValidationOnExistingFormControl =
      isVisible && dynamicFormFieldIndex !== AppConstants.INDEX_NOT_FOUND;

    if (setRequiredValidationOnExistingFormControl) {
      this.setIsFieldRequired(dynamicFormFields[dynamicFormFieldIndex], form.get(fieldKey), isRequired);

      return;
    }

    const addDynamicFormField = isRequired && dynamicFormFieldIndex === AppConstants.INDEX_NOT_FOUND;

    if (addDynamicFormField) {
      const formValidators: FormValidators = { validators: [], asyncValidators: [] };

      const dynamicFormField = this.addEmailDynamicFormField(
        fieldSettingPropertyName,
        ticketHolderSettings,
        isEmailAndConfirmEmailMandatory
      );

      const formControlValidators = this.#dynamicFormFieldValidatorFactory.createDynamicFieldValidators(
        dynamicFormField,
        form,
        formValidators
      );

      const fieldValue: FormFieldsValue = defaultTo(formFields[fieldKey]?.value, '');

      const formControl = new FormControl({ value: fieldValue, disabled: false }, formControlValidators);

      form.addControl(fieldKey, formControl);

      form.addValidators(formValidators.validators);
      form.addAsyncValidators(formValidators.asyncValidators);

      dynamicFormFields.push(dynamicFormField);

      return;
    }

    const fieldIsAlreadyAddedButNotRequired = !isRequired && dynamicFormFieldIndex !== AppConstants.INDEX_NOT_FOUND;
    const removeDynamicFormField = !isVisible && fieldIsAlreadyAddedButNotRequired;

    if (removeDynamicFormField) {
      form.removeControl(fieldKey);

      pullAt(dynamicFormFields, dynamicFormFieldIndex);

      return;
    }
  }

  private addEmailDynamicFormField(
    fieldSettingPropertyName: string,
    ticketHolderSettings: FieldSettings,
    isEmailAndConfirmEmailMandatory: boolean
  ) {
    const createDynamicFormFieldMapper = this.#dynamicFormFieldBuilder.createDynamicFormFieldMapper();
    const createDynamicFormFieldParametersMapper = ticketHolderEmailAndVerifyEmailFormFieldParametersMapper(
      isEmailAndConfirmEmailMandatory
    );

    const createDynamicFormFieldFunction = createDynamicFormFieldMapper[fieldSettingPropertyName];

    const fieldSetting: FieldSetting = {
      isVisible: true,
      isMandatory: true,
      order: ticketHolderSettings[fieldSettingPropertyName].order
    };

    const createDynamicFormFieldParameters = {
      fieldSetting,
      ...createDynamicFormFieldParametersMapper[fieldSettingPropertyName]
    };

    return createDynamicFormFieldFunction(createDynamicFormFieldParameters);
  }

  private createUploadDocumentDynamicFormFields(
    documentUploadType: DocumentUploadType,
    formFields: FormFields,
    order: number
  ): DynamicFormFields {
    const isRequired = documentUploadType === DocumentUploadType.Mandatory;
    const isGDPRConsentChecked = isGDPRConsentFieldChecked(formFields);
    const checkboxOptionLabel = isRequired
      ? 'ticket-assignment.ticket-holder-form.checkbox.gdpr.label.required'
      : 'ticket-assignment.ticket-holder-form.checkbox.gdpr.label.optional';

    const gdprField = this.#dynamicFormFieldBuilder.createGDPRField({
      checkboxHeading: '',
      checkboxOptionLabel,
      order: ++order,
      isGDPRConsentChecked,
      createFormFieldSettings: {
        ...defaultCreateFormFieldSettings,
        isRequired,
        columns: { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }
      }
    });

    const fileUploadField = this.#dynamicFormFieldBuilder.createFileUploadField({
      key: FieldKey.CV,
      label: 'ticket-assignment.ticket-holder-form.file-upload.cv.label',
      tooltip: 'ticket-assignment.ticket-holder-form.file-upload.cv.tooltip',
      order: ++order,
      createFormFieldSettings: {
        ...defaultCreateFormFieldSettings,
        isRequired,
        isDisabled: !isGDPRConsentChecked,
        fileKind: FileKind.CV,
        acceptedFileTypes: [FileType.DOC, FileType.DOCX, FileType.PDF],
        maxFileSize: AppConstants.MAX_FILE_SIZE
      }
    });

    return [gdprField, fileUploadField];
  }

  private enableOrDisableCVFileUploadField(dynamicFormFields: DynamicFormFields, formFields: FormFields) {
    const cvFileUploadDynamicFormField = find(dynamicFormFields, ({ key }) => key === FieldKey.CV);

    if (!isNil(cvFileUploadDynamicFormField)) {
      cvFileUploadDynamicFormField.isDisabled = !isGDPRConsentFieldChecked(formFields);
    }

    return sortBy(dynamicFormFields, 'order');
  }
}
